// src/lib/errors.js
// Centralized error handling and custom error types

/**
 * Base application error class
 */
export class AppError extends Error {
  constructor(message, code = 'UNKNOWN', details = null) {
    super(message);
    this.name = 'AppError';
    this.code = code;
    this.details = details;
    this.userMessage = message; // User-friendly message
  }
}

/**
 * Authentication and authorization errors
 */
export class AuthenticationError extends AppError {
  constructor(message = 'Authentication failed', details = null) {
    super(message, 'AUTH_ERROR', details);
    this.name = 'AuthenticationError';
    this.userMessage = 'Unable to sign in. Please check your credentials and try again.';
  }
}

/**
 * Network and connectivity errors
 */
export class NetworkError extends AppError {
  constructor(message = 'Network request failed', details = null) {
    super(message, 'NETWORK_ERROR', details);
    this.name = 'NetworkError';
    this.userMessage = 'Network connection issue. Please check your internet and try again.';
  }
}

/**
 * Timeout errors
 */
export class TimeoutError extends AppError {
  constructor(message = 'Request timed out', details = null) {
    super(message, 'TIMEOUT', details);
    this.name = 'TimeoutError';
    this.userMessage = 'Request took too long. Please try again.';
  }
}

/**
 * Calendar integration errors
 */
export class CalendarError extends AppError {
  constructor(message = 'Calendar operation failed', details = null) {
    super(message, 'CALENDAR_ERROR', details);
    this.name = 'CalendarError';
    this.userMessage = 'Calendar operation failed. Please reconnect your calendar and try again.';
  }
}

/**
 * Validation errors
 */
export class ValidationError extends AppError {
  constructor(message = 'Validation failed', details = null) {
    super(message, 'VALIDATION_ERROR', details);
    this.name = 'ValidationError';
    this.userMessage = message; // Validation messages are usually user-friendly
  }
}

/**
 * Not found errors
 */
export class NotFoundError extends AppError {
  constructor(message = 'Resource not found', details = null) {
    super(message, 'NOT_FOUND', details);
    this.name = 'NotFoundError';
    this.userMessage = 'The requested resource could not be found.';
  }
}

/**
 * Permission/authorization errors
 */
export class PermissionError extends AppError {
  constructor(message = 'Permission denied', details = null) {
    super(message, 'PERMISSION_DENIED', details);
    this.name = 'PermissionError';
    this.userMessage = 'You do not have permission to perform this action.';
  }
}

/**
 * Convert Supabase error to appropriate AppError type
 */
export function convertSupabaseError(error) {
  if (!error) return null;

  // Handle timeout errors
  if (error.code === 'TIMEOUT') {
    return new TimeoutError(error.message, error.details);
  }

  // Handle not found
  if (error.code === 'PGRST116') {
    return new NotFoundError('Resource not found', error);
  }

  // Handle auth errors
  if (error.message?.includes('JWT') || error.message?.includes('auth')) {
    return new AuthenticationError(error.message, error);
  }

  // Handle validation/constraint errors
  if (error.message?.includes('unique constraint') || error.message?.includes('violates')) {
    return new ValidationError(error.message, error);
  }

  // Handle permission errors
  if (error.code === 'PGRST301' || error.message?.includes('permission')) {
    return new PermissionError(error.message, error);
  }

  // Default to generic app error
  return new AppError(error.message || 'An error occurred', error.code, error);
}

/**
 * Convert Google API error to appropriate AppError type
 */
export function convertGoogleApiError(error) {
  if (!error) return null;

  // Handle auth/token errors
  if (error.message?.includes('401') || error.message?.includes('token')) {
    return new CalendarError('Calendar authorization expired. Please reconnect your calendar.', error);
  }

  // Handle permission errors
  if (error.message?.includes('403') || error.message?.includes('permission')) {
    return new CalendarError('Calendar permissions not granted. Please grant calendar access.', error);
  }

  // Handle not found
  if (error.message?.includes('404')) {
    return new CalendarError('Calendar not found', error);
  }

  // Default to calendar error
  return new CalendarError(error.message || 'Calendar operation failed', error);
}

/**
 * Get user-friendly error message
 */
export function getUserMessage(error) {
  if (!error) return 'An unknown error occurred';

  // If it's our custom error, use userMessage
  if (error.userMessage) {
    return error.userMessage;
  }

  // Try to extract meaningful message from error
  if (error.message) {
    // Clean up technical error messages
    const message = error.message
      .replace(/PGRST\d+:/g, '')
      .replace(/JWT/g, 'authentication')
      .trim();

    return message || 'An error occurred';
  }

  return 'An unknown error occurred';
}

/**
 * Format error for logging (development/debugging)
 */
export function formatErrorForLogging(error) {
  if (!error) return 'Unknown error';

  const parts = [];

  parts.push(`[${error.name || 'Error'}]`);
  parts.push(error.message || 'Unknown error');

  if (error.code) {
    parts.push(`(${error.code})`);
  }

  if (error.details && import.meta.env.VITE_DEBUG) {
    parts.push(`\nDetails: ${JSON.stringify(error.details, null, 2)}`);
  }

  if (error.stack && import.meta.env.VITE_DEBUG) {
    parts.push(`\nStack: ${error.stack}`);
  }

  return parts.join(' ');
}

/**
 * Handle error with logging and optional callback
 */
export function handleError(error, options = {}) {
  const {
    log = true,
    rethrow = false,
    callback = null
  } = options;

  // Log error
  if (log) {
    console.error(formatErrorForLogging(error));
  }

  // Call callback if provided
  if (callback && typeof callback === 'function') {
    callback(error);
  }

  // Re-throw if requested
  if (rethrow) {
    throw error;
  }

  return error;
}
